import { memo, useMemo } from 'react';
import { prepareComboAttributes } from 'src/modules/checkout/prepareComboAttributes.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { getProductUrl } from 'src/utils/getProductUrl.ts';
import { useComposeExcelFile } from 'src/common/hooks/useComposeExcelFile.ts';
import { AccessoryData } from 'src/modules/checkout/CalculateTotals.ts';
import { CheckoutPageData, ComboData } from 'src/modules/checkout/CheckoutPageMapping.ts';
import { getExcelFileName } from 'src/utils/getExcelFileName.ts';
import { DownloadIcon } from 'src/modules/common/icons/DownloadIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { UserSettings } from 'src/types/UserSettings.ts';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate.ts';
import { UserInput } from 'src/modules/user-input/types.ts';
import { numberFormatter } from 'src/utils/numberFormatter.ts';

type Props = {
    userSettings: UserSettings;
    accessories: AccessoryData[];
    actuator: CheckoutPageData['vssAccessories']['actuator'];
    valve: CheckoutPageData['vssAccessories']['valve'];
    combo: ComboData;
    qty: number;
    userInputState: UserInput;
}

export const ValveExportExcelButton = memo(({
    accessories,
    userSettings,
    actuator,
    valve,
    combo,
    qty,
    userInputState,
}: Props) => {
    const t = useBelimoTranslate();

    const table = useMemo(() => {
        const flowData = [
            ...(userInputState.kvs.length > 0 ? [[userInputState.kvs, userInputState.kvs_u]] : []),
            ...(userInputState.v100.length > 0 && userInputState.kvs.length === 0 ? [
                [userInputState.v100, userInputState.v100_u],
            ] : []),
        ];

        const secondSequenceData = [
            ...(userInputState.kvs_2.length > 0 ? [[userInputState.kvs_2, userInputState.kvs_2_u]] : []),
            ...(userInputState.v100_2.length > 0 && userInputState.kvs_2.length === 0 ? [
                [userInputState.v100_2, userInputState.v100_2_u],
            ] : []),
        ];

        const attr = prepareComboAttributes(
            valve,
            actuator,
            (v) => ({ raw: [v], localized: [v] }),
        )
            .reduce(((a, v) => [...a, ...v]), []);

        const clipPos = attr.find(([key]) => key === MESSAGES.clipPosition);

        const valveValues = [
            valve.type,
            valve.pattern,
            valve.fluid,
            valve.operationMode,
            flowData.length === 0 ? '-' : flowData
                .map(([value, unit]) => `${numberFormatter(value, userSettings.currentLocale)} ${
                    t(unit)
                }`).join(', '),
            ...(secondSequenceData.length > 0 ? [
                secondSequenceData
                    .map(([value, unit]) => `${numberFormatter(value, userSettings.currentLocale)} ${
                        t(unit)
                    }`).join(', '),
            ] : []),
            ...attr
                .filter(([key]) => key !== MESSAGES.clipPosition)
                .map(([, value]) => value),
            '',
            ...(clipPos === undefined ? [] : [clipPos[1]]),
        ];

        return {
            columns: [
                MESSAGES.exportExcelMaterial,
                MESSAGES.exportExcelQuantity,
                MESSAGES.Type,
                MESSAGES.Pattern,
                MESSAGES.Fluid,
                MESSAGES.OperationMode,
                MESSAGES.v100,
                ...(secondSequenceData.length > 0 ? [MESSAGES.v100_2] : []),
                ...attr
                    .filter(([key]) => key !== MESSAGES.clipPosition)
                    .map(([key]) => key),
                MESSAGES.Description,
                ...(clipPos === undefined ? [] : [clipPos[0]]),
            ],
            rows: [
                [
                    {
                        text: combo.Label,
                        hyperlink: getProductUrl({
                            countryDomain: userSettings.countryDomain,
                            currentLocale: userSettings.currentLocale,
                            label: combo.Label,
                        }),
                    },
                    String(qty),
                    ...valveValues,
                ],
                ...accessories.map((p) => [
                    {
                        text: p.accessory.item.Label,
                        hyperlink: getProductUrl({
                            countryDomain: userSettings.countryDomain,
                            currentLocale: userSettings.currentLocale,
                            label: p.accessory.item.Label,
                        }),
                    },
                    String(p.qty),
                    ...valveValues.slice(0, clipPos === undefined ? -1 : -2).map(() => ''),
                    p.accessory.item.description,
                ]),
            ],
        };
    }, [accessories, actuator, combo, qty, t, userInputState, userSettings, valve]);

    const downloadExcel = useComposeExcelFile({ table });

    return (
        <Button
            as="a"
            href={downloadExcel ?? ''}
            download={getExcelFileName()}
            loading={downloadExcel === null}
            className="vss-checkout-page__navigation-button"
            size="lg"
            kind="tertiary"
            icon={(
                <div className="vss-checkout-page__navigation-icon">
                    <DownloadIcon/>
                </div>
            )}
        >
            <I18n messageId={MESSAGES.exportExcel}/>
        </Button>
    );
});
